/**
 * «Scegli un run» — the page whose scope is missing. The designer's brief part 1 §4, DEC-85,
 * `docs/adr/9033`.
 *
 * A run-bounded page with no `?runId=` reads nothing (CLAUDE.md §3.4) and used to say so in
 * whatever words its chapter chose, with the picker somewhere else on the page. It now shows one
 * panel: what to choose and why, the latest run NAMED (never opened), the Market and the Run
 * selectors — the Run with the 2px border, because it is the choice to make — and the last three
 * runs as one-line rows, a click on which sets `?runId=`.
 *
 * **No default applies itself.** Nothing here chooses: the latest run is named in a sentence, the
 * Run selector opens on "Choose…", and the Market selector filters the list without scoping any
 * read — it opens on every market the runs are in. `onChoose` fires only on the planner's act.
 *
 * Every word on the panel's surface is `ink-primary`: `ink-secondary` on `strip-choose` measures
 * 4.50:1 in light, on the AA line rather than over it.
 *
 * **One kind of run.** The list is the CALLER's, already reduced to the page's kind (price,
 * distribute, rebalance, consolidation) and market, newest first: the directory that lists them
 * is a chapter's read, and so is the rule that tells one kind from another. This component draws;
 * it neither reads nor classifies.
 */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../lib/format';
import { FOCUS_RING, Skeleton } from '../primitives';
import { ProblemNotice } from './async';
import type { QueryLike } from './read-state';

/** How many runs the panel lists under the selectors. */
export const CHOOSE_RUN_SHOWN = 3;

/** One run on offer, as the caller has already described it. */
export interface ChooseRunOption {
  readonly runId: string;
  /** The run's name as a planner reads it — mono in the list, the option's text in the selector. */
  readonly label: string;
  /** The market it belongs to (`country_key`). */
  readonly market: string;
  /** When it was recorded — ISO 8601. */
  readonly recordedAt: string;
  /** What it holds, already a sentence — "1,126 proposals". `null` when not known yet. */
  readonly volume: string | null;
  /** Its state, already a sentence — "Computed on quarantined stock". `null` when none is said. */
  readonly state: string | null;
}

export interface ChooseRunStateProps {
  /**
   * What the page shows once a run is chosen — "the proposals" — the `{object}` of
   * «Scegli un run per vedere {object}.»
   */
  readonly objectKey: string;
  /** The runs of the page's kind, newest first. A query, so loading and failure are drawn here. */
  readonly runs: QueryLike<readonly ChooseRunOption[]> & { readonly refetch?: () => unknown };
  /** The planner chose a run: write it to the URL (`?runId=`). */
  readonly onChoose: (runId: string) => void;
  /** The zone dates are shown in. Defaults to UTC, the platform's. */
  readonly timeZone?: string;
}

const ALL_MARKETS = '';

export function ChooseRunState({ objectKey, runs, onChoose, timeZone = 'UTC' }: ChooseRunStateProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const marketId = useId();
  const runSelectId = useId();
  const [market, setMarket] = useState(ALL_MARKETS);

  const all = useMemo(() => runs.data ?? [], [runs.data]);
  const markets = useMemo(() => [...new Set(all.map((run) => run.market))].sort(), [all]);
  const offered = market === ALL_MARKETS ? all : all.filter((run) => run.market === market);
  const latest = offered[0];
  const loading = !runs.isError && runs.data === undefined;
  const dateOf = (run: ChooseRunOption): string => formatDateTime(run.recordedAt, i18n.language, timeZone);

  const sentence = runs.isError || loading
    ? null
    : latest === undefined
      ? t(market === ALL_MARKETS ? 'chooseRun.none' : 'chooseRun.noneInMarket', { market })
      : t('chooseRun.latest', { run: latest.label, date: dateOf(latest) });

  return (
    <section
      aria-labelledby={titleId}
      data-choose-run=""
      data-tour="choose-run"
      className="bg-strip-choose border-ink-primary flex flex-col gap-4 border-t-2 px-4 py-4"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id={titleId} className="text-ink-primary text-[15px] font-semibold leading-5">
            {t('chooseRun.title', { object: t(objectKey) })}
          </h2>
          {sentence !== null && <p className="text-ink-primary text-[13px] leading-5">{sentence}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <label
            htmlFor={marketId}
            className="border-ink-primary bg-surface-raised text-ink-primary flex h-[38px] items-center gap-2 border px-3 text-[13px]"
          >
            <span className="text-ink-secondary">{t('chooseRun.market')}</span>
            <select
              id={marketId}
              value={market}
              disabled={markets.length === 0}
              onChange={(event) => setMarket(event.target.value)}
              className={`bg-surface-raised text-ink-primary ${FOCUS_RING}`}
            >
              <option value={ALL_MARKETS}>{t('chooseRun.allMarkets')}</option>
              {markets.map((key) => <option key={key} value={key}>{key}</option>)}
            </select>
          </label>
          <label
            htmlFor={runSelectId}
            className="border-ink-primary bg-surface-raised text-ink-primary flex h-[38px] items-center gap-2 border-2 px-3 text-[13px] font-semibold"
          >
            <span>{t('chooseRun.run')}</span>
            <select
              id={runSelectId}
              value=""
              disabled={offered.length === 0}
              onChange={(event) => { if (event.target.value !== '') onChoose(event.target.value); }}
              className={`bg-surface-raised text-ink-primary max-w-[20rem] ${FOCUS_RING}`}
            >
              <option value="">{t('chooseRun.runPlaceholder')}</option>
              {offered.map((run) => (
                <option key={run.runId} value={run.runId}>{t('chooseRun.option', { run: run.label, date: dateOf(run) })}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {runs.isError ? (
        <ProblemNotice
          error={runs.error}
          titleKey="chooseRun.loadFailed"
          {...(runs.refetch === undefined ? {} : { onRetry: runs.refetch })}
        />
      ) : loading ? (
        <Skeleton rows={CHOOSE_RUN_SHOWN} />
      ) : offered.length === 0 ? null : (
        <table className="w-full border-collapse text-[13px]">
          <caption className="text-ink-primary pb-2 text-start text-[11px] uppercase tracking-[0.08em]">
            {t('chooseRun.recent')}
          </caption>
          <thead className="sr-only">
            <tr>
              <th scope="col">{t('chooseRun.column.run')}</th>
              <th scope="col">{t('chooseRun.column.date')}</th>
              <th scope="col">{t('chooseRun.column.volume')}</th>
              <th scope="col">{t('chooseRun.column.state')}</th>
            </tr>
          </thead>
          <tbody>
            {offered.slice(0, CHOOSE_RUN_SHOWN).map((run, index) => (
              <tr
                key={run.runId}
                data-run-id={run.runId}
                onClick={() => onChoose(run.runId)}
                className={`border-hairline hover:bg-surface-raised h-[38px] cursor-pointer border-b ${index === 0 ? 'border-t-ink-primary border-t' : ''}`}
              >
                <th scope="row" className="py-0 pe-4 text-start font-normal">
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onChoose(run.runId); }}
                    aria-label={t('chooseRun.open', { run: run.label })}
                    className={`font-numeric text-ink-primary ${index === 0 ? 'font-semibold' : ''} ${FOCUS_RING}`}
                  >
                    {run.label}
                  </button>
                </th>
                <td className="text-ink-primary pe-4">{dateOf(run)}</td>
                <td className="text-ink-primary pe-4">{run.volume ?? t('absent.notStated')}</td>
                <td className="text-ink-primary">{run.state ?? t('absent.notStated')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
